import { useEffect, useState } from "react";
import { UserPlus, ArrowRight, ShieldAlert, Coins, Users, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import Modal from "@/components/Modal";
import { btn } from "@/components/Page";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 px-3 py-2 border border-zinc-800 rounded-sm bg-zinc-900/40">
    <Icon size={15} className="text-amber-400" />
    <div>
      <div className="text-sm text-zinc-100 tabular-nums font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  </div>
);

export function ReferralsPanel() {
  const [data, setData] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [uncancelModal, setUncancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [uncancelReason, setUncancelReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    api.get("/admin/referrals").then((r) => setData(r.data)).catch((e) => { toast.error(apiErr(e)); setData({ referrals: [] }); });
  };

  const handleCancelReferral = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason for cancelling");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/referrals/${cancelModal.id}/cancel`, { reason: cancelReason });
      toast.success(`Referral cancelled. Gold deducted from both users.`);
      setCancelModal(null);
      setCancelReason("");
      loadData();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const handleUncancelReferral = async () => {
    if (!uncancelReason.trim()) {
      toast.error("Please provide a reason for restoring");
      return;
    }
    setBusy(true);
    try {
      const response = await api.post(`/admin/referrals/${uncancelModal.id}/uncancel`, { reason: uncancelReason });
      toast.success(`Referral restored. ${response.data.gold_returned_per_user} gold returned to both users.`);
      setUncancelModal(null);
      setUncancelReason("");
      loadData();
    } catch (e) {
      const errorMsg = apiErr(e);
      if (errorMsg.includes("not cancelled")) {
        toast.error("This referral has already been restored. Refreshing list...");
        loadData();
        setUncancelModal(null);
        setUncancelReason("");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="referrals-panel" className="mb-6 border border-amber-900/30 bg-zinc-950 p-4 rounded-sm">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <UserPlus size={16} className="text-amber-400" />
          <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Referrals — Who Recruited Whom</h3>
        </div>
        <button
          onClick={loadData}
          disabled={!data}
          className="text-xs px-2.5 py-1 rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 flex items-center gap-1.5 transition-colors disabled:opacity-50"
          title="Refresh referrals list"
        >
          <RefreshCw size={11} /> Refresh
        </button>
      </div>
      <p className="text-[12px] text-zinc-500 mb-4">Every referral link signup, the milestone tiers each pair has earned, and the rewards granted to both sides.</p>

      {data === null ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : data.referrals.length === 0 ? (
        <p className="text-sm text-zinc-500">No referrals recorded yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            <Stat icon={Users} label="Recruits" value={data.total_recruits} />
            <Stat icon={UserPlus} label="Referrers" value={data.unique_referrers} />
            <Stat icon={Coins} label="Gold paid out" value={(data.total_gold_paid || 0).toLocaleString()} />
            <Stat icon={ShieldAlert} label="Blocked (same IP)" value={data.blocked_count} />
            <Stat icon={XCircle} label="Cancelled" value={data.cancelled_count || 0} />
          </div>

          <div className="border border-zinc-800 rounded-sm overflow-hidden">
            <div className="hidden md:grid grid-cols-[1.4fr_1.4fr_auto_2fr_auto] gap-3 px-3 py-2 bg-zinc-900/70 text-[10px] uppercase tracking-widest text-zinc-500">
              <span>Referrer</span><span>Recruit</span><span>Joined</span><span>Milestones &amp; Rewards</span><span>Actions</span>
            </div>
            <div className="divide-y divide-zinc-800/70 max-h-[28rem] overflow-y-auto">
              {data.referrals.map((r) => (
                <div key={r.id} data-testid={`referral-row-${r.id}`}
                  className={`grid md:grid-cols-[1.4fr_1.4fr_auto_2fr_auto] gap-1 md:gap-3 px-3 py-2.5 items-center ${
                    r.cancelled ? "bg-zinc-900/60 opacity-60" : r.blocked_same_ip ? "bg-red-950/20" : "bg-zinc-950/40"
                  }`}>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate flex items-center gap-1.5">
                      {r.referrer_name}<ArrowRight size={11} className="text-zinc-600 md:hidden" />
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">{r.referrer_email}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-amber-200/90 truncate">{r.recruit_name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{r.recruit_email}</div>
                  </div>
                  <div className="text-[11px] text-zinc-500 tabular-nums">{r.joined ? new Date(r.joined).toLocaleDateString() : "—"}</div>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {r.cancelled && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-800/60 text-zinc-400 border border-zinc-700/50">
                        <XCircle size={10} /> Cancelled (fraud/cheating)
                      </span>
                    )}
                    {r.blocked_same_ip && !r.cancelled && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-red-900/40 text-red-300 border border-red-800/50">
                        <ShieldAlert size={10} /> No payout (same IP)
                      </span>
                    )}
                    {r.tiers.length === 0 && !r.blocked_same_ip && !r.cancelled && <span className="text-[11px] text-zinc-600">No milestones yet</span>}
                    {!r.cancelled && r.tiers.map((t) => (
                      <span key={t.id} title={t.reward}
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-900/25 text-amber-200 border border-amber-800/40">
                        {t.title} <span className="text-amber-400/80">· {t.reward}</span>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    {!r.cancelled && (
                      <button
                        data-testid={`cancel-referral-${r.id}`}
                        onClick={() => setCancelModal(r)}
                        className="text-[11px] px-2 py-1 rounded-sm bg-red-900/30 hover:bg-red-900/50 text-red-300 border border-red-800/40 hover:border-red-700 transition-colors"
                        title="Cancel referral if fraud/cheating detected"
                      >
                        Cancel
                      </button>
                    )}
                    {r.cancelled && (
                      <button
                        data-testid={`restore-referral-${r.id}`}
                        onClick={() => setUncancelModal(r)}
                        className="text-[11px] px-2 py-1 rounded-sm bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-300 border border-emerald-800/40 hover:border-emerald-700 transition-colors"
                        title="Restore referral and return gold"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Cancel Referral Modal */}
          <Modal open={!!cancelModal} onClose={() => { setCancelModal(null); setCancelReason(""); }} title="Cancel Referral">
            {cancelModal && (
              <div className="space-y-4">
                <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded-sm">
                  <div className="text-sm text-zinc-300 mb-1">
                    <span className="text-amber-200">{cancelModal.referrer_name}</span> → <span className="text-amber-200">{cancelModal.recruit_name}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {cancelModal.tiers_count} milestone{cancelModal.tiers_count === 1 ? "" : "s"} granted · {cancelModal.gold_each} gold each
                  </div>
                </div>
                
                <div className="text-sm text-zinc-400">
                  <p className="mb-2">
                    Cancelling this referral will:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-[12px] text-zinc-500">
                    <li>Mark the referral as cancelled (fraud/cheating)</li>
                    <li>Deduct <span className="text-amber-400 font-semibold">{cancelModal.gold_each} gold</span> from both the referrer and recruit (clamped to current balance)</li>
                    <li>Prevent future milestone payouts for this referral</li>
                  </ul>
                </div>

                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Reason for cancellation (required)</label>
                  <textarea
                    data-testid="cancel-reason-input"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="e.g., Same person, multiple accounts, fraud detected..."
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm rounded-sm resize-none"
                  />
                  <div className="text-[10px] text-zinc-600 mt-1">{cancelReason.length}/500</div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    data-testid="confirm-cancel-referral"
                    onClick={handleCancelReferral}
                    disabled={busy || !cancelReason.trim()}
                    className={btn() + " flex-1 bg-red-900 border-red-700 hover:bg-red-800"}
                  >
                    {busy ? "Cancelling..." : "Cancel Referral"}
                  </button>
                  <button
                    onClick={() => { setCancelModal(null); setCancelReason(""); }}
                    disabled={busy}
                    className={btn("ghost") + " flex-1"}
                  >
                    Nevermind
                  </button>
                </div>
              </div>
            )}
          </Modal>

          {/* Restore Referral Modal */}
          <Modal open={!!uncancelModal} onClose={() => { setUncancelModal(null); setUncancelReason(""); }} title="Restore Referral">
            {uncancelModal && (
              <div className="space-y-4">
                <div className="p-3 bg-zinc-900/60 border border-zinc-800 rounded-sm">
                  <div className="text-sm text-zinc-300 mb-1">
                    <span className="text-amber-200">{uncancelModal.referrer_name}</span> → <span className="text-amber-200">{uncancelModal.recruit_name}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    Currently cancelled · {uncancelModal.gold_each} gold was deducted from each user
                  </div>
                </div>
                
                <div className="text-sm text-zinc-400">
                  <p className="mb-2">
                    Restoring this referral will:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-[12px] text-zinc-500">
                    <li>Remove the cancelled status</li>
                    <li>Return <span className="text-emerald-400 font-semibold">{uncancelModal.gold_each} gold</span> to both the referrer and recruit</li>
                    <li>Allow future milestone payouts to resume normally</li>
                    <li>Send restoration notifications to both users</li>
                  </ul>
                </div>

                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Reason for restoration (required)</label>
                  <textarea
                    data-testid="uncancel-reason-input"
                    value={uncancelReason}
                    onChange={(e) => setUncancelReason(e.target.value)}
                    placeholder="e.g., Cancellation was made in error, further review cleared the users..."
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm rounded-sm resize-none"
                  />
                  <div className="text-[10px] text-zinc-600 mt-1">{uncancelReason.length}/500</div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    data-testid="confirm-restore-referral"
                    onClick={handleUncancelReferral}
                    disabled={busy || !uncancelReason.trim()}
                    className={btn() + " flex-1 bg-emerald-900 border-emerald-700 hover:bg-emerald-800"}
                  >
                    {busy ? "Restoring..." : "Restore Referral"}
                  </button>
                  <button
                    onClick={() => { setUncancelModal(null); setUncancelReason(""); }}
                    disabled={busy}
                    className={btn("ghost") + " flex-1"}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
    </div>
  );
}
